import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import {
  PREQUAL_COPY,
  PROCESSING_COMPLETE_HOLD_MS,
  PROCESSING_FAST_FORWARD_MS,
  PROCESSING_STEPS,
  PROCESSING_STEP_TIMERS_MS,
} from "../constants";

interface IPrequalProcessingScreenProps {
  /** True once the prequalify mutation has settled (success or error). */
  isComplete: boolean;
  /** Fired after all steps are checked and the completion hold has elapsed. */
  onFinished: () => void;
}

// Cosmetic checklist shown while the synchronous prequalify mutation is in
// flight. The first steps check off on timers; the last one only checks once
// the mutation settles. If the mutation settles early, remaining steps
// fast-forward so none appears skipped.
export const PrequalProcessingScreen = ({ isComplete, onFinished }: IPrequalProcessingScreenProps) => {
  const [checkedCount, setCheckedCount] = useState(0);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  useEffect(() => {
    if (isComplete) return;
    const timers = PROCESSING_STEP_TIMERS_MS.map((delay, index) =>
      setTimeout(() => {
        setCheckedCount((count) => Math.max(count, index + 1));
      }, delay),
    );
    return () => timers.forEach(clearTimeout);
  }, [isComplete]);

  useEffect(() => {
    if (!isComplete) return;
    if (checkedCount < PROCESSING_STEPS.length) {
      const timer = setTimeout(
        () => setCheckedCount((count) => count + 1),
        PROCESSING_FAST_FORWARD_MS,
      );
      return () => clearTimeout(timer);
    }
    const hold = setTimeout(() => onFinishedRef.current(), PROCESSING_COMPLETE_HOLD_MS);
    return () => clearTimeout(hold);
  }, [isComplete, checkedCount]);

  return (
    <Box className="flex h-full w-full flex-col items-center justify-center px-4">
      <CircularProgress size={56} thickness={3} sx={{ color: "#FEC20B" }} />
      <Typography variant="h4" sx={{ fontWeight: 600, textAlign: "center", mt: 3 }}>
        {PREQUAL_COPY.PROCESSING_TITLE}
      </Typography>
      <Typography variant="body1" sx={{ textAlign: "center", mt: 1, color: "text.secondary" }}>
        {PREQUAL_COPY.PROCESSING_SUBTITLE}
      </Typography>
      <div className="mt-6 flex flex-col gap-3">
        {PROCESSING_STEPS.map((step, index) => {
          const isChecked = index < checkedCount;
          return (
            <div key={step} className="flex items-center gap-2">
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full transition-colors duration-300 ${
                  isChecked ? "bg-[#1D7A6E]" : "bg-[#EBE5DD]"
                }`}
              >
                {isChecked && <CheckIcon sx={{ fontSize: 14, color: "#FFFFFF" }} />}
              </span>
              <span className={`text-sm ${isChecked ? "font-semibold" : "text-gray-1"}`}>
                {step}
              </span>
            </div>
          );
        })}
      </div>
    </Box>
  );
};

export default PrequalProcessingScreen;
